import { getSupabaseClient } from '../../lib/supabase'
import type { Database } from '../../types/database.generated'

export type SupportTicket = Database['public']['Tables']['support_tickets']['Row']
export type SupportComment = Database['public']['Tables']['support_ticket_comments']['Row']
export type CustomerFeedback = Database['public']['Tables']['customer_feedback']['Row']
export type KnowledgeArticle = Database['public']['Tables']['knowledge_articles']['Row']
export type SupportSummary = Database['public']['Views']['support_operations_summary']['Row']

export type AmigoAnswer = {
  answer: string
  sourceIds: string[]
  model: string
}

export async function getSupportWorkspace(organizationId: string) {
  const supabase = getSupabaseClient()
  const [tickets, comments, articles, feedback, summary] = await Promise.all([
    supabase.from('support_tickets').select('*').eq('organization_id', organizationId).order('updated_at', { ascending: false }),
    supabase.from('support_ticket_comments').select('*').order('created_at'),
    supabase.from('knowledge_articles').select('*').eq('is_published', true).order('category').order('title'),
    supabase.from('customer_feedback').select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }),
    supabase.from('support_operations_summary').select('*').eq('organization_id', organizationId).maybeSingle(),
  ])
  const error = tickets.error ?? comments.error ?? articles.error ?? feedback.error ?? summary.error
  if (error) throw error
  return { tickets: tickets.data ?? [], comments: comments.data ?? [], articles: articles.data ?? [], feedback: feedback.data ?? [], summary: summary.data }
}

export async function createSupportTicket(values: Database['public']['Tables']['support_tickets']['Insert']) {
  const { error } = await getSupabaseClient().from('support_tickets').insert(values)
  if (error) throw error
}

export async function addSupportComment(ticketId: string, authorId: string, body: string, isInternal = false) {
  const { error } = await getSupabaseClient().from('support_ticket_comments').insert({ ticket_id: ticketId, author_id: authorId, body, is_internal: isInternal })
  if (error) throw error
}

export async function updateTicket(ticketId: string, values: Database['public']['Tables']['support_tickets']['Update']) {
  const { error } = await getSupabaseClient().from('support_tickets').update(values).eq('id', ticketId)
  if (error) throw error
}

export async function submitFeedback(values: Database['public']['Tables']['customer_feedback']['Insert']) {
  const { error } = await getSupabaseClient().from('customer_feedback').insert(values)
  if (error) throw error
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export async function askAmigo(organizationId: string, question: string): Promise<AmigoAnswer> {
  const response = await getSupabaseClient().functions.invoke('amigo-chat', {
    body: { organizationId, question },
  })
  if (response.error) throw response.error

  const payload: unknown = response.data
  if (!isRecord(payload) || typeof payload.answer !== 'string' || !Array.isArray(payload.sourceIds)) {
    throw new Error('Amigo returned an invalid response')
  }

  return {
    answer: payload.answer,
    sourceIds: payload.sourceIds.filter((id: unknown): id is string => typeof id === 'string'),
    model: typeof payload.model === 'string' ? payload.model : 'OpenAI',
  }
}

export function amigoRecommendations(question: string, articles: KnowledgeArticle[]) {
  const words = question.toLowerCase().split(/[^a-z0-9]+/).filter((word) => word.length > 2)
  return articles
    .map((article) => ({ article, score: words.reduce((score, word) => score + (article.title.toLowerCase().includes(word) || article.summary.toLowerCase().includes(word) || article.keywords.some((keyword) => keyword.includes(word)) ? 1 : 0), 0) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map(({ article }) => article)
}
